import { RequestService } from '../RequestService'
import { useEnvStore } from 'store'

/** KYC认证请求服务 */
class KycRequest extends RequestService {
  constructor() {
    super()
  }

  protected setBaseUrl(): string {
    const envStore = useEnvStore()
    return envStore.config?.appuc || ''
  }

  /**
   * KYC申请 - 提交身份认证申请
   * POST /UCenter/Identity/applyKyc
   */
  applyKyc(data: ApiResponse.KycApplicationData) {
    return this.post<ApiResponse.KycApplicationResponse>('/UCenter/Identity/applyKyc', data)
  }

  /**
   * 推送KYC视频 - 上传活体检测视频 (支持.mp4, .mov格式)
   * POST /UCenter/Identity/pushVideo
   */
  pushVideo(data: { file: File }) {
    // 传递普通对象，让 RequestService 自动处理 FormData
    return this.post<ApiResponse.PushVideoResponse>(
      '/UCenter/Identity/pushVideo',
      { file: data.file },
      {
        excludeFileFromSign: true,
        ignoreSignFields: ['file'],
      },
    )
  }

  /**
   * 推送实名认证ID照片 - 上传身份证件照片
   * POST /UCenter/Identity/pushIdPhoto
   */
  pushIdPhoto(data: { imageFile: File }) {
    // 传递普通对象，让 RequestService 自动处理 FormData；签名忽略 image
    return this.post<ApiResponse.UploadIdPhotoResponse>(
      '/UCenter/Identity/pushIdPhoto',
      { file: data.imageFile },
      {
        ignoreSignFields: ['file'],
        excludeFileFromSign: true,
        isShowError: true,
      },
    )
  }

  /**
   * KYC详情 - 获取用户KYC认证详细信息
   * POST /UCenter/Identity/kycInfo
   */
  getKycInfo() {
    return this.post<ApiResponse.KycDetailInfo>('/UCenter/Identity/kycInfo')
  }

  /**
   * KYC获取随机数 - 用于活体检测验证
   * POST /UCenter/Identity/getIDRandNo
   */
  getIdRandomNumber() {
    return this.post<ApiResponse.GetRandomNumberResponse>('/UCenter/Identity/getIDRandNo')
  }

  /**
   * 读图片 - 获取上传的证件图片
   * GET [动态URL] - 从接口返回的静态资源URL获取
   */
  getUploadedImage(imageUrl: string) {
    return this.get<Blob>(imageUrl)
  }

  /**
   * 获取用户详情 - 获取用户基本信息和KYC状态
   * POST /V3/UCenter/User/getInfo
   */
  getUserInfo() {
    return this.post<ApiResponse.UserKycDetailResponse>('/V3/UCenter/User/getInfo')
  }

  /**
   * KYC身份认证说明 - 获取认证流程说明和要求
   * POST /UCenter/Kyc/kycIntroduction
   */
  getKycIntroduction() {
    return this.post<ApiResponse.KycIntroductionResponse>('/UCenter/Kyc/kycIntroduction')
  }

  /**
   * KYC身份认证说明V2 - 获取认证流程说明和要求V2版本
   * POST /UCenter/Kyc/kycIntroductionV2
   */
  getKycIntroductionV2() {
    return this.post<ApiResponse.ThirdKycInfo[]>('/UCenter/Kyc/kycIntroductionV2')
  }

  /**
   * KYC优化拆分 - 申请KYC V2版本认证
   * POST /UCenter/IdentityV2/applyKyc
   * 支持一次性提交所有信息包括图片文件
   */
  applyKycV2(data: {
    /** 地域标识 static_sms_country 主键 */
    sscId?: string | number
    /** 护照ID卡 */
    passport?: string
    /** 手持证件照 (可选，提交时为URL) */
    holdPhoto?: string
    /** 正面证件图片 (可选) */
    frontPhoto?: string
    /** 背面证件图片 (可选) */
    backPhoto?: string
    /** 证件类型 1 id卡 2 护照 3 驾照 */
    cardType?: number
    /** 视频随机数 (客户端从服务端获取随机数) */
    randNo?: string
    /** 名 */
    firstName?: string
    /** 手持证件视频 (可选，二选一) */
    holdVideo?: File | string
    /** 姓 */
    lastName?: string
    /** 审核等级 必传 1 / 2，其他字段都是可选 */
    level: 1 | 2
  }) {
    // 使用 FormData 处理文件上传
    return this.post<ApiResponse.KycApplicationResponseV2>('/UCenter/IdentityV2/applyKyc', data, {
      excludeFileFromSign: true,
      isShowError: true,
    })
  }

  /**
   * 获取支持的国家列表
   */
  getSupportedCountries() {
    return this.get<ApiResponse.CountryRegion[]>('/UCenter/Identity/getSupportedCountries')
  }

  /**
   * 获取证件类型列表
   */
  getIdDocumentTypes() {
    return this.get<
      {
        type: ApiResponse.IdDocumentType
        name: string
        description: string
        isSupported: boolean
      }[]
    >('/UCenter/Identity/getIdDocumentTypes')
  }

  /**
   * 检查KYC申请状态
   */
  checkKycStatus(applicationId: string) {
    return this.post<ApiResponse.KycApplicationResponse>('/UCenter/Identity/checkStatus', { applicationId })
  }

  /**
   * 取消KYC申请
   */
  cancelKycApplication(applicationId: string) {
    return this.post<{
      success: boolean
      message: string
    }>('/UCenter/Identity/cancelApplication', { applicationId })
  }

  /**
   * 重新提交KYC申请
   */
  resubmitKycApplication(applicationId: string, data: Partial<ApiResponse.KycApplicationData>) {
    return this.post<ApiResponse.KycApplicationResponse>('/UCenter/Identity/resubmit', { applicationId, ...data })
  }

  /**
   * 提交KYC信息 - 获取用户KYC信息用于表单回填
   * POST /UCenter/Kyc/submitKycInfo
   */
  submitKycInfo() {
    return this.post<ApiResponse.SubmitKycInfoResponse>('/UCenter/Kyc/submitKycInfo')
  }
}

/** KYC认证服务 */
export const KycServices = new KycRequest()
